import { maxBy } from 'lodash';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';

/**
 * Impact categories that may not be picked as a key takeaway's SHOWCASE — the single category a
 * takeaway leads with and names drivers for ("the largest positive contribution comes from its X
 * impact, driven by ...").
 *
 * They are large for nearly every company and rarely the distinctive answer in an impact analysis,
 * so leading with one buries whatever is actually specific to the company.
 *
 * The exclusion decides which category is HIGHLIGHTED, never which categories are measured or
 * reported. How far each surface takes it differs, deliberately:
 *
 * - The prose renderers only decline to headline. The sentence still names all three stored
 *   categories, and a suppressed one still gets a connector line to its bar on the chart — the
 *   reader can see it and weigh it themselves.
 * - The net impact MCP payload drops them from `keyTakeaways` outright, because it feeds a
 *   generated summary that leads with whatever it is handed rather than a reader who can look
 *   past the highlight. It costs the caller nothing: the same response carries every category's
 *   ¢/$ figures in `dimensions[]`.
 *
 * Neither surface hides the underlying impact, and neither may start doing so.
 *
 * Neither list may hold more than two entries. A stored takeaway holds three categories, so a
 * third exclusion can empty the eligible set and leave a takeaway with no showcase at all. Every
 * caller guards that case, but the guard degrades the takeaway rather than improving it.
 */
export const SHOWCASE_EXCLUDED_POSITIVE_CATEGORIES: readonly ImpactCategoryId[] = [
  ImpactCategoryId.TAXES,
  ImpactCategoryId.JOBS,
];

/** Negative-side counterpart of {@link SHOWCASE_EXCLUDED_POSITIVE_CATEGORIES}, same two-entry ceiling. */
export const SHOWCASE_EXCLUDED_NEGATIVE_CATEGORIES: readonly ImpactCategoryId[] = [
  ImpactCategoryId.SCARCE_HUMAN_CAPITAL,
];

/** The minimum shape a stored takeaway category needs for a showcase to be picked out of it. */
interface ShowcaseCandidate {
  impactCategory: ImpactCategoryId;
  score: number;
}

/**
 * The category a takeaway should lead with: the highest-scoring one still eligible after the
 * exclusions.
 *
 * Returns undefined when every category is excluded. What a takeaway with no showcase looks like
 * is the caller's decision and differs by surface — prose renderers fall back to a placeholder
 * sentence, the MCP payload simply marks no category.
 */
export function pickShowcaseCategory<T extends ShowcaseCandidate>(
  categories: readonly T[],
  excluded: readonly ImpactCategoryId[],
): T | undefined {
  return maxBy(
    categories.filter(c => !excluded.includes(c.impactCategory)),
    d => d.score,
  );
}
